/**
 * MembersDialog Component - Discussion Group Members List
 *
 * Displays a simple list of trip members with their roles:
 * - Owner (crown icon)
 * - Editor (different color)
 * - Viewer (different color)
 *
 * Features:
 * - Clean, minimal design
 * - Email prefix display (e.g., "tp3@...")
 * - Role-based icon colors
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  WorkspacePremium as CrownIcon, // Crown icon for owner
  Person as PersonIcon
} from '@mui/icons-material';
import { useAuth, app } from '@trip-planner-monorepo/shared';
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * MembersDialog Component
 *
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onClose - Close callback
 * @param {Object} props.trip - Trip data with owner and sharedWith
 * @param {string} props.ownerEmail - Owner's email address
 * @param {string} props.currentUserEmail - Current user's email
 * @param {boolean} props.isSharedTrip - Whether this is a shared trip
 */
const MembersDialog = ({ open, onClose, trip, ownerEmail, currentUserEmail, isSharedTrip }) => {
  const { currentUser } = useAuth();
  const [sharedUsersData, setSharedUsersData] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Fetch shared users with emails when dialog opens
  useEffect(() => {
    const fetchSharedUsers = async () => {
      if (!open || !trip || !trip.sharedWith || trip.sharedWith.length === 0) {
        setSharedUsersData([]);
        return;
      }

      setLoadingUsers(true);
      try {
        const functions = getFunctions(app);
        const getSharedUsersFn = httpsCallable(functions, 'getSharedUsers');

        const tripOwnerId = trip.sharedBy || currentUser?.uid;
        const result = await getSharedUsersFn({
          tripId: trip.id,
          tripOwnerId: tripOwnerId
        });

        setSharedUsersData(result.data.users || []);
        console.log('[MembersDialog] Fetched shared users:', result.data.users);
      } catch (error) {
        console.error('[MembersDialog] Error fetching shared users:', error);
        setSharedUsersData([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchSharedUsers();
  }, [open, trip, currentUser]);

  if (!trip) return null;

  // Helper: Extract email prefix with @...
  const formatEmail = (email) => {
    if (!email) return 'Unknown';
    const prefix = email.split('@')[0];
    return `${prefix}@...`;
  };

  // Build members list with proper logic
  const buildMembersList = () => {
    const members = [];
    const addedUids = new Set(); // Track added UIDs to prevent duplicates

    // 1. Always add owner first
    const ownerUid = trip.sharedBy || currentUser?.uid;
    members.push({
      uid: ownerUid,
      email: ownerEmail || 'Owner',
      role: 'Owner',
      isOwner: true
    });
    if (ownerUid) addedUids.add(ownerUid);

    // 2. Add current user if they're NOT the owner
    if (currentUser?.uid && currentUser.uid !== ownerUid) {
      members.push({
        uid: currentUser.uid,
        email: currentUserEmail,
        role: trip.sharedRole === 'editor' ? 'Editor' : 'Viewer',
        isOwner: false,
        isEditor: trip.sharedRole === 'editor'
      });
      addedUids.add(currentUser.uid);
    }

    // 3. Add other shared users with actual emails from Cloud Function
    if (sharedUsersData && sharedUsersData.length > 0) {
      sharedUsersData.forEach(user => {
        // Skip if already added (owner or current user)
        if (!addedUids.has(user.uid)) {
          // ✅ Use role from Cloud Function response (fresh from Firestore)
          const role = user.role || 'viewer';
          members.push({
            uid: user.uid,
            email: user.email, // ✅ Actual email from Cloud Function!
            role: role === 'editor' ? 'Editor' : 'Viewer',
            isOwner: false,
            isEditor: role === 'editor'
          });
          addedUids.add(user.uid);
        }
      });
    } else if (trip.sharedWith && Array.isArray(trip.sharedWith) && trip.sharedWith.length > 0) {
      // Fallback to UID placeholders if Cloud Function hasn't loaded yet
      trip.sharedWith.forEach(userId => {
        if (!addedUids.has(userId)) {
          const role = trip.roles?.[userId] || 'viewer';
          members.push({
            uid: userId,
            email: `user-${userId.substring(0, 6)}@...`, // Placeholder while loading
            role: role === 'editor' ? 'Editor' : 'Viewer',
            isOwner: false,
            isEditor: role === 'editor'
          });
          addedUids.add(userId);
        }
      });
    }

    return members;
  };

  const members = buildMembersList();

  // Icon colors
  const getIconColor = (member) => {
    if (member.isOwner) return '#FFD700'; // Gold for owner
    if (member.isEditor) return '#1976d2'; // Blue for editor
    return '#757575'; // Gray for viewer
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1
        }}
      >
        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
          Discussion Group Members
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'action.hover' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <List sx={{ py: 0 }}>
          {members.map((member, index) => (
            <ListItem
              key={index}
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: index < members.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider'
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {member.isOwner ? (
                  <CrownIcon
                    sx={{
                      color: getIconColor(member),
                      fontSize: '1.5rem'
                    }}
                  />
                ) : (
                  <PersonIcon
                    sx={{
                      color: getIconColor(member),
                      fontSize: '1.5rem'
                    }}
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: member.isOwner ? 600 : 400,
                        color: 'text.primary'
                      }}
                    >
                      {formatEmail(member.email)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500
                      }}
                    >
                      ({member.role})
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>

        {loadingUsers && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Loading members...
            </Typography>
          </Box>
        )}

        {!loadingUsers && members.length === 1 && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No other members yet
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MembersDialog;
